import React, { useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, Stack, Paper, TextField, Divider,
  IconButton, Tooltip, Chip, Alert, useTheme, Slider,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import MapIcon from '@mui/icons-material/Map';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import CodeIcon from '@mui/icons-material/Code';
import CompressIcon from '@mui/icons-material/Compress';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import MapView from './MapView';
import {
  haversineDistance, calculateBearing, encodePolyline, decodePolyline,
  simplifyRoute, calculateBBox, bboxToPolygon, parseAnyCoordinate, parseAnyGeoFile
} from '../../utils/geoUtils';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';

function GeoJsonToolkit() {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState('distance');

  // ── Distance Calculator State ──
  const [coord1, setCoord1] = useState('40.7128, -74.0060');
  const [coord2, setCoord2] = useState('34.0522, -118.2437');
  const distanceResult = useMemo(() => {
    const p1 = parseAnyCoordinate(coord1);
    const p2 = parseAnyCoordinate(coord2);
    if (!p1 || !p2) return null;
    // geoUtils uses [lon, lat]
    const g1 = [p1[1], p1[0]];
    const g2 = [p2[1], p2[0]];
    const distM = haversineDistance(g1, g2);
    const bearing = calculateBearing(g1, g2);
    return {
      m: distM,
      km: distM / 1000,
      mi: distM / 1609.34,
      bearing,
      p1, p2
    };
  }, [coord1, coord2]);

  // ── Polyline State ──
  const [polylineInput, setPolylineInput] = useState('_p~iF~ps|U_ulLnnqC_mqNvxq`@');
  const [polylineOutput, setPolylineOutput] = useState('');
  const polylineResult = useMemo(() => {
    try {
      if (polylineInput.startsWith('[') || polylineInput.startsWith('{')) {
        // Assume encoding JSON to Polyline
        const result = parseAnyGeoFile(polylineInput);
        if (result.geoJson) {
          const coords = [];
          result.geoJson.features.forEach(f => {
            if (f.geometry.type === 'LineString') coords.push(...f.geometry.coordinates);
            else if (f.geometry.type === 'Point') coords.push(f.geometry.coordinates);
          });
          return { polyline: encodePolyline(coords), type: 'encoded' };
        }
      } else {
        // Decode Polyline to GeoJSON
        const coords = decodePolyline(polylineInput);
        return {
          geoJson: {
            type: 'FeatureCollection',
            features: [{
              type: 'Feature',
              geometry: { type: 'LineString', coordinates: coords },
              properties: { name: 'Decoded Polyline' }
            }]
          },
          type: 'decoded'
        };
      }
    } catch (e) { return null; }
    return null;
  }, [polylineInput]);

  // ── Simplifier State ──
  const [simplifyInput, setSimplifyInput] = useState('');
  const [tolerance, setTolerance] = useState(10); // meters
  const simplifyResult = useMemo(() => {
    const res = parseAnyGeoFile(simplifyInput);
    if (!res.geoJson) return null;
    const simplifiedFeatures = res.geoJson.features.map(f => {
      if (f.geometry.type === 'LineString') {
        return {
          ...f,
          geometry: {
            ...f.geometry,
            coordinates: simplifyRoute(f.geometry.coordinates, tolerance)
          }
        };
      }
      return f;
    });
    const simplifiedFc = { ...res.geoJson, features: simplifiedFeatures };
    
    // Count points
    const countPoints = (fc) => fc.features.reduce((sum, f) => {
      if (f.geometry.type === 'LineString') return sum + f.geometry.coordinates.length;
      return sum + 1;
    }, 0);

    return {
      geoJson: simplifiedFc,
      originalCount: countPoints(res.geoJson),
      simplifiedCount: countPoints(simplifiedFc)
    };
  }, [simplifyInput, tolerance]);

  // ── BBox State ──
  const [bboxInput, setBboxInput] = useState('');
  const bboxResult = useMemo(() => {
    const res = parseAnyGeoFile(bboxInput);
    if (!res.geoJson) return null;
    const allCoords = [];
    res.geoJson.features.forEach(f => {
      if (f.geometry.type === 'LineString') allCoords.push(...f.geometry.coordinates);
      else if (f.geometry.type === 'Point') allCoords.push(f.geometry.coordinates);
      else if (f.geometry.type === 'Polygon') f.geometry.coordinates.forEach(ring => allCoords.push(...ring));
    });
    const bbox = calculateBBox(allCoords);
    if (!bbox) return null;
    return {
      bbox,
      polygon: {
        type: 'FeatureCollection',
        features: [bboxToPolygon(bbox), ...res.geoJson.features]
      }
    };
  }, [bboxInput]);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'distance':
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Distance Calculator</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Calculate the great-circle distance and bearing between two geographic points.
            </Typography>
            <Stack spacing={3}>
              <TextField
                label="Point A (Lat, Lon)"
                value={coord1}
                onChange={(e) => setCoord1(e.target.value)}
                placeholder="40.7128, -74.0060"
                fullWidth
              />
              <TextField
                label="Point B (Lat, Lon)"
                value={coord2}
                onChange={(e) => setCoord2(e.target.value)}
                placeholder="34.0522, -118.2437"
                fullWidth
              />
              {distanceResult && (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>Results</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Kilometers</Typography>
                      <Typography variant="h6">{distanceResult.km.toFixed(2)} km</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Miles</Typography>
                      <Typography variant="h6">{distanceResult.mi.toFixed(2)} mi</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Bearing</Typography>
                      <Typography variant="h6">{distanceResult.bearing.toFixed(1)}°</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Meters</Typography>
                      <Typography variant="h6">{Math.round(distanceResult.m).toLocaleString()} m</Typography>
                    </Box>
                  </Box>
                </Paper>
              )}
            </Stack>
          </Box>
        );
      case 'polyline':
        return (
          <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>Polyline Encoder / Decoder</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Convert between Google Polyline strings and GeoJSON/Coordinate arrays.
            </Typography>
            <TextField
              multiline rows={6} fullWidth
              label="Input (Polyline string or GeoJSON)"
              value={polylineInput}
              onChange={(e) => setPolylineInput(e.target.value)}
              sx={{ fontFamily: 'monospace', mb: 2 }}
            />
            {polylineResult && (
              <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <Button
                    size="small" variant="outlined" startIcon={<ContentCopyIcon />}
                    onClick={() => handleCopy(polylineResult.type === 'encoded' ? polylineResult.polyline : JSON.stringify(polylineResult.geoJson, null, 2))}
                  >
                    Copy Output
                  </Button>
                </Stack>
                {polylineResult.type === 'decoded' ? (
                  <Paper variant="outlined" sx={{ flexGrow: 1, minHeight: 200 }}>
                    <MapView mapId="polyline-map" geoJsonData={polylineResult.geoJson} />
                  </Paper>
                ) : (
                  <TextField
                    multiline rows={4} fullWidth readOnly
                    value={polylineResult.polyline}
                    sx={{ fontFamily: 'monospace', bgcolor: 'action.hover' }}
                  />
                )}
              </Box>
            )}
          </Box>
        );
      case 'simplify':
        return (
          <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>Route Simplifier</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Reduce the number of points in a GeoJSON LineString while preserving its shape.
            </Typography>
            <TextField
              multiline rows={6} fullWidth
              label="GeoJSON Input"
              value={simplifyInput}
              onChange={(e) => setSimplifyInput(e.target.value)}
              placeholder='Paste GeoJSON here...'
              sx={{ fontFamily: 'monospace', mb: 2 }}
            />
            <Box sx={{ px: 2, mb: 3 }}>
              <Typography variant="caption" gutterBottom>Tolerance: {tolerance} meters</Typography>
              <Slider
                value={tolerance}
                onChange={(_, v) => setTolerance(v)}
                min={1} max={500} step={1}
                valueLabelDisplay="auto"
              />
            </Box>
            {simplifyResult && (
              <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                  <Chip label={`Original: ${simplifyResult.originalCount} pts`} size="small" />
                  <Chip label={`Simplified: ${simplifyResult.simplifiedCount} pts`} color="primary" size="small" />
                  <Chip label={`${Math.round((1 - simplifyResult.simplifiedCount / simplifyResult.originalCount) * 100)}% reduced`} color="success" size="small" />
                </Stack>
                <Paper variant="outlined" sx={{ flexGrow: 1, minHeight: 200 }}>
                  <MapView mapId="simplify-map" geoJsonData={simplifyResult.geoJson} />
                </Paper>
              </Box>
            )}
          </Box>
        );
      case 'bbox':
        return (
          <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>Bounding Box Generator</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Calculate the minimum bounding box (envelope) for your spatial data.
            </Typography>
            <TextField
              multiline rows={6} fullWidth
              label="GeoJSON or Coordinate Input"
              value={bboxInput}
              onChange={(e) => setBboxInput(e.target.value)}
              placeholder='Paste GeoJSON or coordinates here...'
              sx={{ fontFamily: 'monospace', mb: 2 }}
            />
            {bboxResult && (
              <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>Bounding Box Coordinates</Typography>
                  <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                    South West: {bboxResult.bbox.minLat.toFixed(6)}, {bboxResult.bbox.minLon.toFixed(6)}
                    <br />
                    North East: {bboxResult.bbox.maxLat.toFixed(6)}, {bboxResult.bbox.maxLon.toFixed(6)}
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ flexGrow: 1, minHeight: 200 }}>
                  <MapView mapId="bbox-map" geoJsonData={bboxResult.polygon} />
                </Paper>
              </Box>
            )}
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <SEO
        title="GeoJSON Toolkit | Distance, Polyline, Simplifier | UmbrellaCo"
        description="A comprehensive toolkit for geospatial developers. Calculate distances, encode/decode polylines, simplify routes, and generate bounding boxes online."
        keywords="distance calculator, polyline encoder, route simplifier, bounding box generator, geojson tools"
        url="/spatial-tools/geojson-toolkit"
        jsonLd={createSoftwareAppSchema(
          'GeoJSON Toolkit',
          'Professional tools for geospatial data processing and analysis.',
          'https://umbrellaco.in/spatial-tools/geojson-toolkit',
          ['Distance Calculation', 'Polyline Encoding', 'Path Simplification']
        )}
      />

      <Box sx={{ px: 2, pt: 1, pb: 1, flexShrink: 0 }}>
        <Typography component="h1" variant="h6" fontWeight={700}>GeoJSON Toolkit</Typography>
      </Box>

      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        <Box sx={{
          width: 220, borderRight: `1px solid ${theme.palette.divider}`,
          display: 'flex', flexDirection: 'column', bgcolor: 'background.paper'
        }}>
          {[
            { id: 'distance', label: 'Distance Calc', icon: <SquareFootIcon /> },
            { id: 'polyline', label: 'Polyline Tool', icon: <CodeIcon /> },
            { id: 'simplify', label: 'Route Simplifier', icon: <CompressIcon /> },
            { id: 'bbox', label: 'BBox Generator', icon: <AspectRatioIcon /> },
          ].map((tab) => (
            <Button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              variant={activeTab === tab.id ? 'contained' : 'text'}
              startIcon={tab.icon}
              sx={{
                justifyContent: 'flex-start', py: 1.5, px: 2, borderRadius: 0,
                textTransform: 'none', fontWeight: activeTab === tab.id ? 700 : 500,
                bgcolor: activeTab === tab.id ? 'primary.main' : 'transparent',
                color: activeTab === tab.id ? 'primary.contrastText' : 'text.primary',
                '&:hover': {
                  bgcolor: activeTab === tab.id ? 'primary.dark' : 'action.hover',
                }
              }}
            >
              {tab.label}
            </Button>
          ))}
        </Box>

        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
          {renderContent()}
        </Box>
      </Box>
    </Box>
  );
}

export default GeoJsonToolkit;
